/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { useMemo } from 'react';

import { useTranslateMessage } from '../localization';
import { Box, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

/**
 * Monthly AI credit usage bar (cds-ai-service usage-summary).
 *
 * variant: 'bar' (default, full-width row with progress) or 'chip' (compact header pill).
 */
const CommandIntelligenceCreditsBar = ({ usageSummary, status, variant = 'bar' }) => {
  const theme = useTheme();
  const translateMessage = useTranslateMessage();

  const { slice, scopeLabelId, monthKey } = useMemo(() => {
    if (!usageSummary || typeof usageSummary !== 'object') {
      return { slice: null, scopeLabelId: null, monthKey: '' };
    }

    const mk = typeof usageSummary.monthKey === 'string' ? usageSummary.monthKey : '';
    const prof = usageSummary.profile;

    if (prof && typeof prof === 'object') {
      return {
        slice: prof,
        scopeLabelId: 'command_intelligence_credits_scope_profile',
        monthKey: mk,
      };
    }

    const ent = usageSummary.entity;

    if (ent && typeof ent === 'object') {
      return {
        slice: ent,
        scopeLabelId: 'command_intelligence_credits_scope_entity',
        monthKey: mk,
      };
    }

    return { slice: null, scopeLabelId: null, monthKey: mk };
  }, [usageSummary]);

  const barColor = useMemo(() => {
    const pct = slice?.percentUsed;

    if (typeof pct !== 'number' || Number.isNaN(pct)) {
      return theme.palette.primary.main;
    }

    if (pct >= 92) {
      return theme.palette.error.main;
    }

    if (pct >= 78) {
      return theme.palette.warning.main;
    }

    return theme.palette.primary.main;
  }, [slice?.percentUsed, theme.palette]);

  const tooltipTitle =
    monthKey !== ''
      ? translateMessage('command_intelligence_credits_tooltip', { monthKey })
      : translateMessage('command_intelligence_credits_tooltip_short');

  const renderChipShell = (label, color, tip) => (
    <Tooltip
      title={tip}
      arrow
      placement="bottom"
      enterDelay={150}
      disableInteractive={false}
      PopperProps={{
        sx: { zIndex: t => t.zIndex.tooltip + 1 },
        modifiers: [{ name: 'preventOverflow', options: { boundary: 'viewport' } }],
      }}
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: 320,
            fontSize: '0.72rem',
            lineHeight: 1.4,
            px: 1.25,
            py: 0.75,
            whiteSpace: 'normal',
          },
        },
      }}
    >
      <Box
        component="span"
        tabIndex={0}
        role="status"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.25,
          borderRadius: 1,
          bgcolor: alpha(color, 0.12),
          color,
          fontSize: '0.7rem',
          fontWeight: 600,
          lineHeight: 1.2,
          cursor: 'help',
          outline: 'none',
          transition: 'background-color 120ms ease',
          '&:hover': {
            bgcolor: alpha(color, 0.2),
          },
          '&:focus-visible': {
            boxShadow: t => `0 0 0 2px ${alpha(t.palette.primary.main, 0.4)}`,
          },
        }}
      >
        {label}
      </Box>
    </Tooltip>
  );

  if (status === 'loading' || status === 'idle') {
    if (variant === 'chip') {
      return renderChipShell(
        translateMessage('command_intelligence_credits_loading'),
        theme.palette.text.secondary,
        translateMessage('command_intelligence_credits_loading')
      );
    }

    return (
      <Box
        sx={{
          px: 2.5,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {translateMessage('command_intelligence_credits_loading')}
        </Typography>
      </Box>
    );
  }

  if (status === 'error' || !slice) {
    if (variant === 'chip') {
      return renderChipShell(
        '—',
        theme.palette.text.secondary,
        translateMessage('command_intelligence_credits_unavailable')
      );
    }

    return (
      <Box
        sx={{
          px: 2.5,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {translateMessage('command_intelligence_credits_unavailable')}
        </Typography>
      </Box>
    );
  }

  const used = typeof slice.creditsUsed === 'number' && !Number.isNaN(slice.creditsUsed) ? slice.creditsUsed : 0;
  const cap = slice.monthlyCreditLimit;
  const hasCap = typeof cap === 'number' && !Number.isNaN(cap) && cap > 0;
  const pct = typeof slice.percentUsed === 'number' && !Number.isNaN(slice.percentUsed) ? slice.percentUsed : 0;

  const usedFmt = used.toLocaleString();
  const capFmt = hasCap ? cap.toLocaleString() : '';

  const summaryText = hasCap
    ? translateMessage('command_intelligence_credits_used_of_cap', { used: usedFmt, cap: capFmt })
    : translateMessage('command_intelligence_credits_used_no_cap', { used: usedFmt });

  const scopeText = scopeLabelId ? translateMessage(scopeLabelId) : '';

  if (variant === 'chip') {
    const label = hasCap ? `${Math.round(pct)}%` : usedFmt;
    const tip = `${tooltipTitle} \u00b7 ${summaryText}`;

    return renderChipShell(label, barColor, tip);
  }

  return (
    <Tooltip title={tooltipTitle} arrow placement="top" enterDelay={400}>
      <Box
        sx={{
          px: 2.5,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
          cursor: 'default',
        }}
      >
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
            {translateMessage('command_intelligence_credits_monthly_title')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
            {scopeText ? `${scopeText} · ${summaryText}` : summaryText}
          </Typography>
        </Stack>

        {hasCap ? (
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.max(0, pct))}
            sx={{
              height: 6,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.text.primary, 0.08),
              '& .MuiLinearProgress-bar': {
                borderRadius: 1,
                bgcolor: barColor,
              },
            }}
          />
        ) : null}
      </Box>
    </Tooltip>
  );
};

export default CommandIntelligenceCreditsBar;
